// CompanyVerificationSceneDOM.js - Company Email Verification with Template Implementation
// Integrates mobile-company-verification.html template

import BaseMenuScene from "./BaseMenuScene.js";
import { auth } from "../firebaseConfig.js";

export default class CompanyVerificationSceneDOM extends BaseMenuScene {
    constructor() {
        super({ key: "CompanyVerificationSceneDOM" });
        this.domElement = null;
        this.currentStep = 'email'; // email, verifying, verified, error
        this.verificationData = null;
    }

    async create() {
        console.log('🏢 CompanyVerificationSceneDOM: Creating company verification with template');
        
        // Create the verification interface
        this.createVerificationInterface();
        
        // Setup event listeners
        this.setupEventListeners();
        
        this.sceneReady = true;
    }

    createVerificationInterface() {
        // Remove existing overlay
        const existingOverlay = document.getElementById('company-verification-scene-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        const overlay = document.createElement('div');
        overlay.id = 'company-verification-scene-overlay';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow: auto;
            font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif;
            color: #ffffff;
            padding: 20px;
        `;

        // Load template CSS
        this.loadTemplateCSS();

        overlay.innerHTML = this.createVerificationHTML();

        document.body.appendChild(overlay);
        this.domElement = overlay;
    }

    loadTemplateCSS() {
        if (!document.getElementById('company-verification-css')) {
            const link = document.createElement('link');
            link.id = 'company-verification-css';
            link.rel = 'stylesheet';
            link.href = '/company-verification.css';
            document.head.appendChild(link);
        }
    }

    createVerificationHTML() {
        return `
            <!-- Company Verification Template Structure -->
            <header class="header">
                <div></div> <!-- Spacer -->
                <button class="skip-button" data-action="skip">Skip for now</button>
            </header>
            
            <!-- Progress Bar -->
            <div class="progress-container">
                <div class="progress-bar" style="width: ${this.getProgressPercentage()}%"></div>
            </div>
            
            <div class="verification-container">
                <!-- Status Badge -->
                <div class="status-badge ${this.getStatusClass()}">
                    <span>${this.getStatusIcon()}</span>
                    <span>${this.getStatusText()}</span>
                </div>
                
                <!-- Verification Card -->
                <div class="verification-card">
                    <div class="card-icon">${this.getStepIcon()}</div>
                    <h2 class="card-title">${this.getStepTitle()}</h2>
                    <p class="card-description">${this.getStepDescription()}</p>
                    
                    ${this.getStepContent()}
                </div>
                
                <!-- Benefits Section -->
                <div class="benefits-section">
                    <h3 class="benefits-title">Company Verification Benefits</h3>
                    <div class="benefits-list">
                        <div class="benefit-item">
                            <span class="benefit-icon">🏢</span>
                            <div class="benefit-content">
                                <h4 class="benefit-title">Firm-Specific Training</h4>
                                <p class="benefit-description">Access specialized modules for your company</p>
                            </div>
                        </div>
                        
                        <div class="benefit-item">
                            <span class="benefit-icon">📊</span>
                            <div class="benefit-content">
                                <h4 class="benefit-title">Company Analytics</h4>
                                <p class="benefit-description">Compare performance with colleagues</p>
                            </div>
                        </div>
                        
                        <div class="benefit-item">
                            <span class="benefit-icon">🏆</span>
                            <div class="benefit-content">
                                <h4 class="benefit-title">Firm Leaderboards</h4>
                                <p class="benefit-description">Compete within your organization</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    getProgressPercentage() {
        switch (this.currentStep) {
            case 'email': return 25;
            case 'verifying': return 50;
            case 'verified': return 100;
            case 'error': return 75;
            default: return 0;
        }
    }

    getStatusClass() {
        switch (this.currentStep) {
            case 'verified': return 'verified';
            case 'error': return 'error';
            default: return '';
        }
    }

    getStatusIcon() {
        switch (this.currentStep) {
            case 'verified': return '✅';
            case 'error': return '❌';
            case 'verifying': return '⏳';
            default: return '📧';
        }
    }

    getStatusText() {
        switch (this.currentStep) {
            case 'email': return 'Email Verification Required';
            case 'verifying': return 'Verification in Progress';
            case 'verified': return 'Company Verified';
            case 'error': return 'Verification Failed';
            default: return 'Email Verification';
        }
    }

    getStepIcon() {
        switch (this.currentStep) {
            case 'email': return '🏢';
            case 'verifying': return '⏳';
            case 'verified': return '✅';
            case 'error': return '❌';
            default: return '🏢';
        }
    }

    getStepTitle() {
        switch (this.currentStep) {
            case 'email': return 'Verify Your Company Email';
            case 'verifying': return 'Verification in Progress';
            case 'verified': return 'Company Verified!';
            case 'error': return 'Verification Failed';
            default: return 'Company Verification';
        }
    }

    getStepDescription() {
        switch (this.currentStep) {
            case 'email': return 'We need to verify your company email to unlock firm-specific features and analytics.';
            case 'verifying': return 'Please check your email and click the verification link we sent you.';
            case 'verified': return 'Your company email has been successfully verified. You now have access to all firm features.';
            case 'error': return 'We couldn\'t verify your company email. Please try again or contact support.';
            default: return '';
        }
    }

    getStepContent() {
        switch (this.currentStep) {
            case 'email':
                return `
                    <form class="verification-form" data-step="email">
                        <div class="form-group">
                            <label class="form-label">Company Email Address</label>
                            <input type="email" class="form-input" id="company-email" placeholder="you@company.com" required>
                            <small class="form-help">Use your official company email address</small>
                        </div>
                        
                        <button type="submit" class="btn-primary full-width">
                            Send Verification Email
                        </button>
                    </form>
                `;
            case 'verifying':
                return `
                    <div class="verification-status">
                        <div class="status-animation">
                            <div class="pulse-circle"></div>
                        </div>
                        <p class="status-message">
                            Verification email sent to:<br>
                            <strong>${this.verificationData?.email || 'your email'}</strong>
                        </p>
                        <div class="verification-actions">
                            <button class="btn-secondary" data-action="resend">Resend Email</button>
                            <button class="btn-primary" data-action="check-status">Check Status</button>
                        </div>
                    </div>
                `;
            case 'verified':
                return `
                    <div class="success-content">
                        <div class="success-animation">
                            <div class="checkmark">✓</div>
                        </div>
                        <p class="success-message">
                            Welcome to <strong>${this.verificationData?.companyName || 'your company'}</strong>!<br>
                            You now have access to all firm-specific features.
                        </p>
                        <button class="btn-primary full-width" data-action="continue">
                            Continue to Dashboard
                        </button>
                    </div>
                `;
            case 'error':
                return `
                    <div class="error-content">
                        <div class="error-message">
                            <p>We couldn't verify your company email. This might be because:</p>
                            <ul class="error-reasons">
                                <li>The email domain is not recognized</li>
                                <li>The verification link expired</li>
                                <li>There was a technical issue</li>
                            </ul>
                        </div>
                        <div class="error-actions">
                            <button class="btn-secondary" data-action="try-again">Try Again</button>
                            <button class="btn-primary" data-action="contact-support">Contact Support</button>
                        </div>
                    </div>
                `;
            default:
                return '';
        }
    }

    setupEventListeners() {
        // Navigation actions
        this.domElement.querySelectorAll('[data-action]').forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleAction(button.dataset.action);
            });
        });

        // Form submissions
        this.domElement.querySelectorAll('form').forEach(form => {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.handleFormSubmit(form.dataset.step);
            });
        });
    }

    handleAction(action) {
        console.log('🏢 CompanyVerificationSceneDOM: Handling action:', action);
        
        switch (action) {
            case 'skip':
                this.skipVerification();
                break;
            case 'resend':
                this.resendVerificationEmail();
                break;
            case 'check-status':
                this.checkVerificationStatus();
                break;
            case 'continue':
                this.continueToApp();
                break;
            case 'try-again':
                this.tryAgain();
                break;
            case 'contact-support':
                this.contactSupport();
                break;
        }
    }

    async handleFormSubmit(step) {
        console.log('🏢 Company verification form submitted:', step);
        
        switch (step) {
            case 'email':
                await this.sendVerificationEmail();
                break;
        }
    }

    async sendVerificationEmail() {
        const email = this.domElement.querySelector('#company-email').value;
        
        if (!email) {
            this.showNotification('Please enter your company email address', 'error');
            return;
        }

        try {
            // Integrate with existing firm verification system
            const { sendCompanyVerificationEmail } = await import("../services/firmAuthService.js");
            
            this.verificationData = { email, companyName: this.extractCompanyName(email) };
            
            // Send verification email
            await sendCompanyVerificationEmail(email);
            
            this.currentStep = 'verifying';
            this.createVerificationInterface();
            this.showNotification('Verification email sent!', 'success');
        } catch (error) {
            console.error('Company verification error:', error);
            this.currentStep = 'error';
            this.createVerificationInterface();
            this.showNotification('Error sending verification email', 'error');
        }
    }

    extractCompanyName(email) {
        const domain = email.split('@')[1];
        const name = domain.split('.')[0];
        return name.charAt(0).toUpperCase() + name.slice(1);
    }

    async resendVerificationEmail() {
        if (this.verificationData?.email) {
            await this.sendVerificationEmail();
        }
    }

    async checkVerificationStatus() {
        try {
            // Check if user's email is now verified
            await auth.currentUser.reload();
            if (auth.currentUser.emailVerified) {
                this.currentStep = 'verified';
                this.createVerificationInterface();
                this.showNotification('Company email verified!', 'success');
            } else {
                this.showNotification('Email not yet verified. Please check your inbox.', 'info');
            }
        } catch (error) {
            console.error('Error checking verification status:', error);
            this.showNotification('Error checking verification status', 'error');
        }
    }

    skipVerification() {
        console.log('🏢 Skipping company verification');
        this.continueToApp();
    }

    continueToApp() {
        this.startScene('AuthenticatedMenuSceneDOM');
    }

    tryAgain() {
        this.currentStep = 'email';
        this.createVerificationInterface();
    }

    contactSupport() {
        // Open support contact method
        this.showNotification('Support contact feature coming soon', 'info');
    }

    showNotification(message, type = 'info') {
        console.log(`🏢 Notification (${type}):`, message);
        if (window.showGeneralMessage) {
            window.showGeneralMessage(message, 3000, type === 'error');
        }
    }

    shutdown() {
        if (this.domElement) {
            this.domElement.remove();
            this.domElement = null;
        }
        super.shutdown();
    }
}
